import { AgmGlobals } from './globals';
import { Observable, interval, timer, OperatorFunction, fromEvent, merge, of } from 'rxjs';

import * as webstomp from 'webstomp-client';
import * as SockJS from 'sockjs-client';
import * as moment from 'moment';
import { forwardRef } from '@angular/core';
import { mapTo } from 'rxjs/operators';
import { Entity } from 'projects/app-misc/src/app/modules/data/abstract-entity.store';

export function loadScript(url): Promise<string> {
    return Observable.create(observer => {
        console.log('preparing to load...')
        const node = document.createElement('script');
        node.src = url;
        node.type = 'text/javascript';
        node.onload = (() => {
            observer.next(url);
            observer.complete();
        });
        node.onerror = (() => {
            observer.error(`Couldn't load script ${url}`);
        });
        document.getElementsByTagName('head')[0].appendChild(node);
    })
        .toPromise();
}

export function isDevice(): boolean {
    const w = window as any;
    // return (typeof w.PhoneGap !== 'undefined' || typeof w.Cordova !== 'undefined' || typeof w.cordova !== 'undefined');
    return !!w.isCordovaDevice;
}

export function openNavigator(destination, successCallback: () => any = null, errorCallback: () => any = null, options: any = {}): void {
    const w = window as any;
    const emptycb = () => { }

    if (w.launchnavigator) {
        w.launchnavigator.navigate(destination, successCallback || emptycb, errorCallback || emptycb, options);
    }
    else {
        console.log('openNavigator for ' + destination);
    }
}

export function openURL(url): Window {
    if (isDevice()) {
        return window.open(url, '_blank', 'location=yes,enableViewportScale=yes,transitionstyle=coververtical,closebuttoncaption=Close');
    }
    else {
        return window.open(url);
    }
}

export function addWindowCloseCallBack(w: Window, cb: () => any) {
    if (isDevice()) {
        w.addEventListener('exit', cb, false);
    } else {
        const subscription = interval(500)
            .subscribe(time => {
                if (w.closed) {
                    cb();
                    subscription.unsubscribe();
                }
            });
    }
}

export function connectToSocket(endPoint: string, cb: (frame?: webstomp.Frame) => any, headers: webstomp.ConnectionHeaders, autoReconnect: boolean): webstomp.Client {
    const socket = new SockJS(endPoint);
    const stompClient = webstomp.over(socket, {
        binary: false,
        heartbeat: { outgoing: 10000, incoming: 10000 },
        protocols: undefined,
        debug: false
    })

    const defaultHeader: webstomp.ConnectionHeaders = {};
    const errorCb = ((error: CloseEvent | webstomp.Frame) => {
        console.log(error)
        if (autoReconnect) {
            timer(10 * 1000)
                .subscribe(() => {
                    connectToSocket(endPoint, cb, headers, autoReconnect);
                });
        };
    });

    stompClient.connect(Object.assign(defaultHeader, headers || {}), cb, errorCb);

    return stompClient;
}

export function getBodyHeight(): number {
    const body = document.body,
        html = document.documentElement;

    return Math.max(body.scrollHeight, body.offsetHeight, html.clientHeight, html.scrollHeight, html.offsetHeight);
}

export function parseDate(dateString: string): Date {
    return moment(dateString).toDate();
    // return clearTimeZone(d);
}

export function clearTimeZone(d: Date): Date {
    d.setTime(d.getTime() + d.getTimezoneOffset() * 60 * 1000);

    return d;
}

export function addTimeZone(date: Date): Date {
    const d = new Date(date);
    d.setTime(d.getTime() - d.getTimezoneOffset() * 60 * 1000);

    return d;
}

export function getGlobals(): AgmGlobals {
    const scope = window;
    const propertyName = '_agm_globals';
    scope[propertyName] = scope[propertyName] || new AgmGlobals();
    return scope[propertyName];
}

export function findTarget(target, selector, depth = 100): any {
    do {
        if (target.matches(selector)) {
            return target;
        }
        target = target.parentNode;
        --depth
    } while (target && target.matches && depth);
}

export function numberRange(from: number, to: number): number[] {
    return Array.from({ length: to - from + 1 }, (value, key) => key + from);
}

export function objectValues<T>(o: Object): T[] {
    return Object.keys(o).map(function (key) {
        return o[key];
    });
}

export function removeFromArray(myArray: any, key: any) {
    const index = myArray.indexOf(key, 0);
    if (index > -1) {
        myArray.splice(index, 1);
    }
}

function compareDates(date1: Date, date2: Date, order: 'asc' | 'desc', orderNulls: 'asc' | 'desc'): number {
    let ord = 1;
    if (order === 'desc') {
        ord = -1
    }

    let ordNulls = 1;
    if (orderNulls === 'desc') {
        ordNulls = -1;
    }

    if (date1 && date2) {
        const t1 = date1.getTime();
        const t2 = date2.getTime();
        return t1 > t2 ? 1 * ord : (t1 < t2 ? -1 * ord : 0);
    }
    else {
        return date1 ? 1 * ordNulls : (date2 ? -1 * ordNulls : 0);
    }
}

export function compareDatesAsc(date1: Date, date2: Date): number {
    return compareDates(date1, date2, 'asc', 'asc')
}

export function compareDatesDesc(date1: Date, date2: Date): number {
    return compareDates(date1, date2, 'desc', 'desc')
}

export function compareDatesAscNullsLast(date1: Date, date2: Date): number {
    return compareDates(date1, date2, 'asc', 'desc')
}

export function compareDatesDescNullsFirst(date1: Date, date2: Date): number {
    return compareDates(date1, date2, 'desc', 'asc')
}

export function groupPerfomanceInfo(objectArray) {
    return objectArray.reduce((acc, obj) => {
        const key = obj.name;
        if (!acc[key]) {
            acc[key] = {
                count: 0,
                totalDuration: 0,
                items: []
            };
        }
        ++acc[key].count
        acc[key].totalDuration += obj.duration
        acc[key].items.push(obj);
        return acc;
    }, {});
}
(window as any).groupPerfomanceInfo = groupPerfomanceInfo;

export function provideParent(parentType: any, component: any): any {
    return { provide: parentType, useExisting: forwardRef(() => component) };
}

export function navigatorOnline$() {
    return merge(
        fromEvent(window, 'offline').pipe(mapTo(false)),
        fromEvent(window, 'online').pipe(mapTo(true)),
        of(navigator.onLine),
    );
}

export function getValue(obj: any, path: string): any {
    const paths = path.split('.');
    let current = obj;
    let i;

    for (i = 0; i < paths.length; ++i) {
        if (current[paths[i]] === undefined || current[paths[i]] === null) {
            return current[paths[i]];
        } else {
            current = current[paths[i]];
        }
    }
    return current;
}

export function upperCaseValue(v: string): string {
    if (v) {
        return v.toUpperCase();
    }

    return v
}

export function capitalizeCaseValue(v: string): string {
    if (v) {
        return v.charAt(0).toUpperCase() + v.slice(1);
    }

    return v
}


export function trackEntityById(index: number, entity: Entity): number {
    return entity.id;
}
